import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import libFloat from "../libFloat";
import LibDateTime from "../libDateTime";
import { formatarCpfCnpj } from "../documento";

const PRIMARY = [60, 95, 151];
const AZUL_SUAVE = [235, 240, 250];
const GRAY_LIGHT = [245, 247, 250];
const GRAY_BORDER = [196, 194, 194];
const GRAY_TEXT = [107, 114, 128];
const GRAY_DARK = [31, 41, 55];
const VERDE = [22, 101, 52];
const VERMELHO = [185, 28, 28];
const MARGEM = 12;

const SITUACOES = [
	["Autorizadas", "emitidas"],
	["Canceladas", "canceladas"],
	["Denegadas", "denegadas"],
	["Inutilizadas", "inutilizadas"],
	["Rejeitadas / com erro", "rejeitadas"],
	["Pendentes (em aberto)", "pendentes"],
];

const IMPOSTOS = [
	["bc_icms", "BC ICMS"],
	["valor_icms", "ICMS"],
	["bc_icms_st", "BC ST"],
	["valor_icms_st", "ICMS ST"],
	["valor_fcp", "FCP"],
	["valor_fcp_st", "FCP ST"],
	["valor_icms_deson", "ICMS deson."],
	["valor_icms_mono", "ICMS mono."],
	["valor_cred_sn", "Créd. SN"],
	["valor_difal", "DIFAL"],
	["valor_fcp_difal", "FCP DIFAL"],
	["valor_ipi", "IPI"],
	["valor_pis", "PIS"],
	["valor_cofins", "COFINS"],
	["bc_ibscbs", "BC IBS/CBS"],
	["valor_ibs_uf", "IBS UF"],
	["valor_ibs_mun", "IBS Mun."],
	["valor_cbs", "CBS"],
];

const moeda = (valor) => libFloat.strToCurrency(valor || 0);

const somar = (lista, campo) => lista.reduce((total, item) => total + (item[campo] || 0), 0);

const alinharADireita = (inicio, quantidade) =>
	Object.fromEntries(Array.from({ length: quantidade }, (_, i) => [inicio + i, { halign: "right" }]));

const rotulo = (texto) => ({ content: texto, styles: { fontStyle: "bold", textColor: PRIMARY } });

function cabecalho(doc, { modelo, competencia, empresa }) {
	const largura = doc.internal.pageSize.getWidth();

	doc.setFont("helvetica", "bold");
	doc.setFontSize(8.5);
	doc.setTextColor(...PRIMARY);
	doc.text("Emitido em", largura - MARGEM, 15, { align: "right" });

	doc.setFont("helvetica", "normal");
	doc.setFontSize(7.5);
	doc.setTextColor(...GRAY_TEXT);
	doc.text(LibDateTime.formatDateToSaoPaulo(new Date(), "dd/MM/yyyy HH:mm"), largura - MARGEM, 19.5, { align: "right" });

	doc.setFont("helvetica", "bold");
	doc.setFontSize(14);
	doc.setTextColor(...PRIMARY);
	doc.text("Fechamento Fiscal", MARGEM, 16);

	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(...GRAY_TEXT);
	doc.text(`Competência ${competencia}`, MARGEM, 21.5);

	doc.setDrawColor(...PRIMARY);
	doc.setLineWidth(0.4);
	doc.line(MARGEM, 26, MARGEM + 70, 26);

	autoTable(doc, {
		startY: 31,
		body: [
			[rotulo("Empresa:"), empresa.razao_social || "—", rotulo("CNPJ:"), empresa.cnpj ? formatarCpfCnpj(empresa.cnpj) : "—"],
			[
				rotulo("Documento:"),
				modelo,
				rotulo("Ambiente:"),
				empresa.ambiente === "HOMOLOGACAO" ? "Homologação (sem valor fiscal)" : "Produção",
			],
		],
		margin: { left: MARGEM, right: MARGEM },
		theme: "plain",
		styles: { fontSize: 7.5, cellPadding: 0.8, textColor: GRAY_DARK },
		columnStyles: {
			0: { cellWidth: 20 },
			1: { cellWidth: "auto" },
			2: { cellWidth: 16 },
			3: { cellWidth: 50 },
		},
	});

	return doc.lastAutoTable.finalY + 5;
}

function secao(doc, titulo, y) {
	const largura = doc.internal.pageSize.getWidth();

	doc.setFillColor(...GRAY_LIGHT);
	doc.rect(MARGEM, y, largura - MARGEM * 2, 7, "F");

	doc.setFont("helvetica", "bold");
	doc.setFontSize(8.5);
	doc.setTextColor(...PRIMARY);
	doc.text(titulo, MARGEM + 2, y + 4.8);

	return y + 8.5;
}

function tabela(doc, y, opcoes) {
	autoTable(doc, {
		startY: y,
		margin: { left: MARGEM, right: MARGEM },
		theme: "grid",
		styles: { fontSize: 7.5, cellPadding: 1.5, lineColor: GRAY_BORDER, lineWidth: 0.1, textColor: GRAY_DARK },
		headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: "bold" },
		alternateRowStyles: { fillColor: GRAY_LIGHT },
		footStyles: { fillColor: AZUL_SUAVE, textColor: PRIMARY, fontStyle: "bold", halign: "right" },
		showFoot: "lastPage",
		...opcoes,
	});

	return doc.lastAutoTable.finalY + 6;
}

function semRegistros(doc, texto, y) {
	doc.setFont("helvetica", "italic");
	doc.setFontSize(7.5);
	doc.setTextColor(...GRAY_TEXT);
	doc.text(texto, MARGEM + 2, y + 3);

	return y + 9;
}

function rodape(doc) {
	const largura = doc.internal.pageSize.getWidth();
	const altura = doc.internal.pageSize.getHeight();
	const totalPaginas = doc.internal.getNumberOfPages();

	for (let pagina = 1; pagina <= totalPaginas; pagina++) {
		doc.setPage(pagina);
		doc.setFont("helvetica", "normal");
		doc.setFontSize(7);
		doc.setTextColor(...GRAY_TEXT);
		doc.text("Portal SZ Sistemas - Fechamento Fiscal", MARGEM, altura - 6);
		doc.text(`Página ${pagina} de ${totalPaginas}`, largura - MARGEM, altura - 6, { align: "right" });
	}
}

export function gerarPdfFechamentoFiscal({ modelo, competencia, empresa, dados }) {
	const doc = new jsPDF("p", "mm", "a4");
	const impostos = IMPOSTOS.filter(([campo]) => somar(dados.cfops, campo) !== 0);
	const situacoes = SITUACOES.filter(([, campo]) => dados[campo] > 0).map(([texto, campo]) => [texto, dados[campo]]);

	let y = cabecalho(doc, { modelo, competencia, empresa });

	y = secao(doc, "Resumo do período", y);
	y = situacoes.length
		? tabela(doc, y, {
				head: [["Situação", "Quantidade"]],
				body: situacoes,
				foot: [[{ content: `Valor total autorizado: ${moeda(dados.valorAutorizado)}`, colSpan: 2 }]],
				columnStyles: { 1: { halign: "right", cellWidth: 35 } },
			})
		: semRegistros(doc, "Nenhuma nota no período.", y);

	y = secao(doc, "Totais por CFOP", y);
	y = dados.cfops.length
		? tabela(doc, y, {
				head: [["CFOP", "CST / CSOSN", "Valor", ...impostos.map(([, titulo]) => titulo)]],
				body: dados.cfops.map((cfop) => [cfop.cfop, cfop.cst, moeda(cfop.valor), ...impostos.map(([campo]) => moeda(cfop[campo]))]),
				foot: [[{ content: "Total", colSpan: 2 }, moeda(somar(dados.cfops, "valor")), ...impostos.map(([campo]) => moeda(somar(dados.cfops, campo)))]],
				columnStyles: { 0: { cellWidth: 20 }, ...alinharADireita(2, impostos.length + 1) },
			})
		: semRegistros(doc, "Nenhuma nota autorizada no período.", y);

	if (dados.lacunas.length)
		y = tabela(doc, secao(doc, "Quebras de sequência", y), {
			head: [["Série", "Do número", "Ao número", "Quantidade"]],
			body: dados.lacunas.map(({ serie, de, ate }) => [serie, de, ate, ate - de + 1]),
			columnStyles: {
				0: { halign: "center", cellWidth: 20 },
				1: { halign: "right" },
				2: { halign: "right" },
				3: { halign: "right", cellWidth: 35 },
			},
		});

	if (dados.notas.length)
		tabela(doc, secao(doc, "Notas do período", y), {
			head: [["Série", "Número", "Emissão", "Chave de acesso", "Cliente", "Situação", "Valor"]],
			body: dados.notas.map((nota) => [
				nota.serie,
				nota.numero,
				nota.emissao,
				nota.chave,
				nota.cliente,
				nota.situacao,
				moeda(nota.valor),
			]),
			foot: [[{ content: `Total autorizado: ${moeda(dados.valorAutorizado)}`, colSpan: 7 }]],
			columnStyles: {
				0: { halign: "center", cellWidth: 10 },
				1: { halign: "right", cellWidth: 14 },
				2: { cellWidth: 26 },
				3: { cellWidth: 66, fontSize: 7 },
				5: { cellWidth: 19 },
				6: { halign: "right", cellWidth: 22 },
			},
			didParseCell: (celula) => {
				if (celula.section !== "body" || celula.column.index !== 5) return;
				celula.cell.styles.textColor = dados.notas[celula.row.index].autorizada ? VERDE : VERMELHO;
				celula.cell.styles.fontStyle = "bold";
			},
		});

	rodape(doc);

	return doc;
}
